// Las piezas de la ventana «Santi», calcadas de la propuesta del 03-11 (docs/pelotas/ux-2026-11-03/propuesta/
// herramientas/estilo.css): quién (S, P, el robot del agente), los globos de la conversación, el separador de día, la
// raya «Nuevo», el punto blanco de lo nuevo y la línea que ve un socio en lugar de lo que solo envía Pau. Sin oro aquí:
// el oro es solo «Enviar».
import { Bot } from 'lucide-react';
import type { ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { inicialDe, nombreDe } from './tiempo';

const TAMANO = {
  24: 'h-6 w-6 text-[14px]',
  32: 'h-8 w-8 text-[15px]',
  40: 'h-10 w-10 text-[18px]',
} as const;

/** P (Pau), S (Santi): un círculo gris con la inicial. */
export function AvatarPersona({ persona, tamano = 32, className }: { persona: string; tamano?: keyof typeof TAMANO; className?: string }) {
  return (
    <span
      className={cn('relative inline-flex shrink-0 items-center justify-center rounded-full bg-oficina-hover font-semibold text-oficina-texto ring-[1.5px] ring-inset ring-white/15', TAMANO[tamano], className)}
      aria-label={nombreDe(persona)}
      role="img"
    >
      {inicialDe(persona)}
    </span>
  );
}

/** El agente: un robot sobre un cuadrado redondeado. */
export function AvatarAgente({ tamano = 32, className }: { tamano?: 32 | 40; className?: string }) {
  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center bg-oficina-elevada text-oficina-texto ring-1 ring-inset ring-white/10', tamano === 40 ? 'h-10 w-10 rounded-xl' : 'h-8 w-8 rounded-[10px]', className)} aria-hidden>
      <Bot className={tamano === 40 ? 'h-[22px] w-[22px]' : 'h-[18px] w-[18px]'} strokeWidth={1.8} />
    </span>
  );
}

/** El punto blanco de «Santi te ha escrito y no lo has visto» (el de toda la oficina para lo nuevo). */
export function PuntoNuevo({ className }: { className?: string }) {
  return <i className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-oficina-texto', className)} aria-label={t('oficinaV2.pelotas.lista.nuevo')} role="img" data-testid="punto-nuevo" />;
}

/** «112/120»: cuántas letras van de las que caben, solo cerca del límite (desde el 90 %); en rojo al llegar. */
export function Contador({ n, max, className }: { n: number; max: number; className?: string }) {
  if (n < Math.floor(max * 0.9)) return null;
  return (
    <span className={cn('shrink-0 text-[14px] leading-5 tabular-nums', n >= max ? 'font-semibold text-oficina-error' : 'text-oficina-texto3', className)} aria-live="polite" data-testid="contador">
      {t('oficinaV2.pelotas.contador', { n, max })}
    </span>
  );
}

/** «31 de octubre» / «Ayer» / «Hoy»: una píldora blanca al 6 % en el centro de la conversación. */
export function SeparadorDia({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 mt-5 flex justify-center" data-testid="separador-dia">
      <span className="rounded-full bg-white/[0.06] px-3 py-1 text-[14px] font-medium leading-5 text-oficina-texto2">{children}</span>
    </div>
  );
}

/** «Nuevo»: dónde empieza lo que Santi escribió desde la última vez que Pau lo abrió (una raya fina, la palabra a la derecha). */
export function RayaNuevo() {
  return (
    <div className="mt-3 flex items-center gap-3 text-[14px] font-semibold leading-5 text-oficina-texto before:h-px before:flex-1 before:bg-oficina-texto/[0.22]" data-testid="raya-nuevo">
      {t('oficinaV2.pelotas.ventana.nuevo')}
    </div>
  );
}

/** Un globo de la conversación: Santi a la izquierda (gris, con su «S» en el último de cada grupo), Pau a la derecha (oro oscuro). */
export function Globo({ de, hora: h, children, ultimo = true, className }: { de: 'pau' | 'santi'; hora?: string; children: ReactNode; ultimo?: boolean; className?: string }) {
  const mio = de === 'pau';
  return (
    <div className={cn('flex items-end gap-2', ultimo ? 'mt-2' : 'mt-1', mio ? 'justify-end pl-[20%]' : 'pr-12', className)} data-testid={`globo-${de}`}>
      {!mio ? <span className="w-8 shrink-0">{ultimo ? <AvatarPersona persona="santi" /> : null}</span> : null}
      <div
        className={cn(
          'relative max-w-[680px] rounded-[18px] px-3.5 py-2 text-[16px] leading-6 shadow-sm ring-1 ring-white/[0.075]',
          mio ? 'bg-oficina-burbuja-pau text-white' : 'bg-oficina-hover text-oficina-texto',
          ultimo && (mio ? 'rounded-br' : 'rounded-bl'),
        )}
      >
        <span className="whitespace-pre-wrap break-words">{children}</span>
        {h ? <span className={cn('float-right ml-3.5 mt-1 text-[14px] leading-5 tabular-nums', mio ? 'text-oficina-meta-pau' : 'text-oficina-texto3')}>{h}</span> : null}
      </div>
    </div>
  );
}

/**
 * 02-11 — Lo que ve un socio en lugar de lo que sale hacia Santi como Pau (la caja «Escribe a Santi…», los botones de una
 * tarjeta). Un socio lo lee todo en la oficina; esto solo lo envía Pau (el servidor contesta 403 SOLO_PAU a cualquier
 * otra sesión). Sustituye a la acción, nunca la enseña desactivada (cursor-ux-mirror §2.1).
 */
export function LoEnviaPau({ className }: { className?: string }) {
  return (
    <p className={cn('flex min-h-11 items-center gap-2 text-[15px] leading-5 text-oficina-texto2', className)} data-testid="lo-envia-pau">
      {t('oficinaV2.pelotas.loEnviaPau')}
    </p>
  );
}
